import { merge } from "../core/Merge.js";
import { Signal } from "../core/Signal.js";
import { FullscreenUtil } from "../display/FullscreenUtil.js";
import { Calc } from "../math/Calc.js";

export type ActorEvent = MouseEvent | TouchEvent | PointerEvent;

export enum PointerState {
    NONE = 0,
    START = 1 << 0,
    MOVE = 1 << 1,
    END = 1 << 2,
    CANCEL = 1 << 3,
    ALL = START | MOVE | END | CANCEL,
}

export enum PointerType {
    NONE = 0,
    MOUSE = 1 << 4,
    TOUCH = 1 << 5,
    ALL = MOUSE | TOUCH,
}

export type PointerFlagFunction = (
    e: ActorEvent,
    state: PointerState,
    type: PointerType
) => boolean;

export type PointerFlag =
    | boolean
    | PointerState
    | PointerType
    | PointerFlagFunction;

export class Pointer {
    public id: number = 0;
    public sessionId: number = 0;
    public state: PointerState = PointerState.NONE;
    public event: ActorEvent = null;
    public x: number = 0;
    public y: number = 0;
    public originX: number = 0;
    public originY: number = 0;
    public moveX: number = 0;
    public moveY: number = 0;
    public deltaTime: number = 0;
    public deltaX: number = 0;
    public deltaY: number = 0;
    /**
     * Velocity in px/ms (pixel per millisecond)
     * in client space.
     */
    public velocityX: number = 0;
    /**
     * Velocity in px/ms (pixel per millisecond)
     * in client space.
     */
    public velocityY: number = 0;
    public distance: number = 0;
    public creationTime: number = 0;
    public changeTime: number = 0;
    public multitouch: boolean = false;

    public intervalTime: number = 0;
    public intervalX: number = 0;
    public intervalY: number = 0;
}

export interface ActorOptions {
    maxPointers?: number;
    filter?: PointerFlag;
    /**
     * Call stopPropagation() for the events.
     * If the given value is a boolean value,
     * all events are effected. To better control
     * which events should be effected, use flags.
     */
    stopPropagation?: PointerFlag;
    useCapture?: boolean;
    passive?: boolean;
    /**
     * Call preventDefault() for the events.
     * If the given value is a boolean value,
     * all events are effected. To better control
     * which events should be effected, use flags.
     */
    preventDefault?: PointerFlag;
    allowedMouseButtons?: number[];
}

export var ActorNonOptions: any = {};

interface PointerData {
    clientX: number;
    clientY: number;
}

interface EventToState {
    [eventType: string]: PointerState;
}

interface EventToType {
    [eventType: string]: PointerType;
}

export class Actor<
    PointerInfo extends Pointer = Pointer,
    Options extends ActorOptions = ActorOptions
> {
    public static DEFAULT: ActorOptions = {
        maxPointers: -1,
        stopPropagation: false,
        preventDefault: false,
        filter: true,
        useCapture: false,
        passive: false,
        allowedMouseButtons: [0],
    };

    public onDown = Signal.create<[PointerInfo[], Actor<PointerInfo>]>();
    public onMove = Signal.create<[PointerInfo[], Actor<PointerInfo>]>();
    public onUp = Signal.create<[PointerInfo[], Actor<PointerInfo>]>();
    public onClear = Signal.create();
    public onStart = Signal.create<[PointerInfo[], Actor<PointerInfo>]>();
    public onChangeActive = Signal.create<[boolean]>();

    public maxPointers: number = -1;
    public stopPropagation: PointerFlag = false;
    public preventDefault: PointerFlag = false;
    public filter: PointerFlag = false;
    public allowedMouseButtons: number[] = [0];
    public pointer: PointerInfo;

    protected _pointers: PointerInfo[] = [];

    private _down: (e: ActorEvent) => void;
    private _move: (e: ActorEvent) => void;
    private _up: (e: ActorEvent) => void;
    private _cancel: () => void;
    private _useCapture: boolean = false;
    private _passive: boolean = false;
    private _element: HTMLElement = null;
    private _enabled: boolean = true;
    private _mouseEventId: number = -1;
    private _eventTargets: HTMLElement[] = [];
    private _disposables: DisposableObject[] = [];

    private static _eventToPointerState: EventToState = {
        touchstart: PointerState.START,
        touchmove: PointerState.MOVE,
        touchend: PointerState.END,
        touchcancel: PointerState.CANCEL,
        mousedown: PointerState.START,
        mousemove: PointerState.MOVE,
        mouseup: PointerState.END,
    };

    private static _eventToPointerType: EventToType = {
        touchstart: PointerType.TOUCH,
        touchmove: PointerType.TOUCH,
        touchend: PointerType.TOUCH,
        touchcancel: PointerType.TOUCH,
        mousedown: PointerType.MOUSE,
        mousemove: PointerType.MOUSE,
        mouseup: PointerType.MOUSE,
    };

    private static _sessionId: number = 1;

    constructor(element: HTMLElement = null, options: Options = null) {
        this.pointer = this.createPointer();

        var lastTouchClientX = 0;
        var lastTouchClientY = 0;
        var lastTouchTime = 0;

        this._down = (e: ActorEvent) => {
            if (!this._enabled || !this.applyFilter(e)) return;

            this.applyStopPropagation(e);
            this.applyPreventDefault(e);

            //Prevent adding mouse event when a touch event fired already
            //for that position and timespan.
            if (e.type.indexOf("mouse") !== -1) {
                //TODO: in new browser the delay could be much smaller!
                var delay = this.getTime() - lastTouchTime;
                if (delay < 500) {
                    var mouseEvent = e as MouseEvent;
                    var dx = mouseEvent.clientX - lastTouchClientX;
                    var dy = mouseEvent.clientY - lastTouchClientY;
                    var dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < 20) {
                        return;
                    }
                }
            }

            var length = this.count;
            //TODO: clamp multiple touch pointers per one touch event.
            if (this.maxPointers != -1 && length >= this.maxPointers) {
                return;
            }

            var infos = this.addPointer(e);
            if (infos) {
                this.updateGlobalPointer(PointerState.START, infos, e);
                if (this.pointers.length === 1) {
                    this.onStart.dispatch(infos, this);
                }
                if (!this.onDown) {
                    return;
                }

                this.onDown.dispatch(infos, this);
            }

            if (infos) {
                let t: HTMLElement = e.target as HTMLElement;

                /**
                 * If an element is removed on start, then
                 * the "touchmove" etc. are possibly never
                 * dispatched!
                 * The target muss be the "real" target!
                 */
                // if(e.composed && !document.body.contains(t)){
                if (e.composed) {
                    let path = e.composedPath();
                    if (path.length > 0) {
                        t = path[0] as HTMLElement; //real target
                    }
                }

                var isListeningForTarget =
                    this._eventTargets.indexOf(t) !== -1 && t != this._element;
                if (length === 0 || !isListeningForTarget) {
                    if (!isListeningForTarget) {
                        this._eventTargets.push(t);
                    }
                    this.addEventListenerActive(t, length);
                }

                if (length === 0) {
                    this.onChangeActive.dispatch(true);
                }
            }
        };

        this._move = (e: ActorEvent) => {
            if (!this.applyFilter(e)) {
                return;
            }

            this.applyStopPropagation(e);
            this.applyPreventDefault(e);

            var infos = this.movePointer(e);
            if (infos) {
                this.updateGlobalPointer(PointerState.MOVE, infos, e);
                this.onMove.dispatch(infos, this);
            } else {
            }
        };

        this._up = (e: ActorEvent) => {
            if (!this.applyFilter(e)) {
                //To keep the pointer list in sync,
                //we have to remove the pointer anyway.
                this.removePointer(e);
                return;
            }

            this.applyStopPropagation(e);
            this.applyPreventDefault(e);

            var infos = this.removePointer(e);
            if (infos) {
                this.updateGlobalPointer(PointerState.END, infos, e);
                this.onUp.dispatch(infos, this);
            }

            if (infos && this.count === 0) {
                if (e.type.indexOf("touch") !== -1) {
                    var touchEvent = e as TouchEvent;
                    lastTouchClientX = touchEvent.changedTouches[0].clientX;
                    lastTouchClientY = touchEvent.changedTouches[0].clientY;
                    lastTouchTime = this.getTime();
                }
                this.removeEventListenerActive();
                this.onChangeActive.dispatch(false);
                this.onClear.dispatch();
            }
        };

        this._cancel = () => {
            this.cancel();
        };

        this._disposables.push(
            FullscreenUtil.onChange.add((isFullscreen) => {
                this._cancel();
            })
        );

        this.element = element;
        if (options !== ActorNonOptions) this.setOptions(options);
    }

    private checkFlag(flag: PointerFlag, e: ActorEvent): boolean {
        if (typeof flag === "boolean") return flag;
        var state: PointerState = Actor._eventToPointerState[e.type];
        var type: PointerType = Actor._eventToPointerType[e.type];

        if (typeof flag === "function") {
            return flag(e, state, type);
        }

        var hasStateFlag = Boolean(flag & PointerState.ALL);
        var hasTypeFlag = Boolean(flag & PointerType.ALL);
        if (hasTypeFlag && hasStateFlag)
            return Boolean(flag & state) && Boolean(flag & type);
        if (hasTypeFlag) return Boolean(flag & type);
        if (hasStateFlag) return Boolean(flag & state);
        return false;
    }

    private applyFilter(e: ActorEvent): boolean {
        return this.checkFlag(this.filter, e);
    }

    private applyPreventDefault(e: ActorEvent): boolean {
        if (!e.cancelable) return false;
        var applied = this.checkFlag(this.preventDefault, e);
        if (applied) {
            try {
                e.preventDefault();
            } catch (error) {
                return false;
            }
        }
        return applied;
    }

    private applyStopPropagation(e: ActorEvent): boolean {
        var applied = this.checkFlag(this.stopPropagation, e);
        if (applied) {
            try {
                e.stopPropagation();
            } catch (error) {
                return false;
            }
        }
        return applied;
    }

    protected createPointer(): PointerInfo {
        const p = new Pointer() as PointerInfo;
        if (this._pointers.length === 0) {
            Actor._sessionId++;
            p.sessionId = Actor._sessionId;
        } else {
            p.sessionId = this._pointers[0].sessionId;
        }

        return p;
    }

    private updateGlobalPointer(
        state: PointerState,
        pointers: PointerInfo[],
        event: ActorEvent
    ): void {
        var client = Actor.center(this._pointers);
        var data: PointerData = {
            clientX: client ? client.x : 0,
            clientY: client ? client.y : 0,
        };

        this.pointer.sessionId = pointers[0]?.sessionId || 0;

        if (state === PointerState.START) {
            this.pointer.x = data.clientX;
            this.pointer.y = data.clientY;
            this.updatePointer(this.pointer, state, data, event);
        } else if (state === PointerState.MOVE) {
            this.updatePointer(this.pointer, state, data, event);
        } else if (
            state === PointerState.END ||
            state === PointerState.CANCEL
        ) {
            // this.pointer.x = data.clientX;
            // this.pointer.y = data.clientY;
            this.updatePointer(
                this.pointer,
                state,
                this._pointers.length > 0 ? data : null,
                event
            );
        }
    }

    public start(e: ActorEvent): void {
        this.cancel();
        this._down(e);
    }

    public cancel(): void {
        if (this._pointers.length === 0) {
            return;
        }

        for (var pointer of this._pointers) {
            pointer.state = PointerState.CANCEL;
            this.updatePointer(
                pointer,
                PointerState.CANCEL,
                {
                    clientX: pointer.x,
                    clientY: pointer.y,
                },
                pointer.event
            );
        }
        var pointers = this._pointers.slice();
        this._pointers = [];
        this.onUp.dispatch(pointers, this);
        this.removeEventListenerActive();
        this.onChangeActive.dispatch(false);
        this.onClear.dispatch();
    }

    public setOptions(options: ActorOptions) {
        options = merge({}, Actor.DEFAULT, options);
        this.maxPointers = options.maxPointers;
        this.allowedMouseButtons = options.allowedMouseButtons;
        this.stopPropagation = options.stopPropagation;
        this.preventDefault = options.preventDefault;
        this.useCapture = options.useCapture;
        this.passive = options.passive;
        this.filter = options.filter;
    }

    private addEventListener(): void {
        if (this._element) {
            this._element.addEventListener(
                "mousedown",
                this._down,
                this.getEventListenerOptions()
            );
            this._element.addEventListener(
                "touchstart",
                this._down,
                this.getEventListenerOptions()
            );
        }
    }

    private removeEventListener(): void {
        if (this._element) {
            this._element.removeEventListener(
                "mousedown",
                this._down,
                this.getEventListenerOptions()
            );
            this._element.removeEventListener(
                "touchstart",
                this._down,
                this.getEventListenerOptions()
            );
        }
        this.removeEventListenerActive();
    }

    private addEventListenerActive(
        eventTarget: HTMLElement,
        length: number
    ): void {
        if (length === 0) {
            document.addEventListener(
                "mousemove",
                this._move,
                this.getEventListenerOptions()
            );
            document.addEventListener(
                "mouseup",
                this._up,
                this.getEventListenerOptions()
            );
            window.addEventListener(
                "dragend",
                this._cancel,
                this.getEventListenerOptions()
            );
            window.addEventListener(
                "blur",
                this._cancel,
                this.getEventListenerOptions()
            );
        }

        eventTarget.addEventListener(
            "touchmove",
            this._move,
            this.getEventListenerOptions()
        );
        eventTarget.addEventListener(
            "touchend",
            this._up,
            this.getEventListenerOptions()
        );
        eventTarget.addEventListener(
            "touchcancel",
            this._up,
            this.getEventListenerOptions()
        );
    }

    private removeEventListenerActive(): void {
        document.removeEventListener(
            "mousemove",
            this._move,
            this.getEventListenerOptions()
        );
        document.removeEventListener(
            "mouseup",
            this._up,
            this.getEventListenerOptions()
        );
        window.removeEventListener(
            "dragend",
            this._cancel,
            this.getEventListenerOptions()
        );
        window.removeEventListener(
            "blur",
            this._cancel,
            this.getEventListenerOptions()
        );

        for (var target of this._eventTargets) {
            target.removeEventListener(
                "touchmove",
                this._move,
                this.getEventListenerOptions()
            );
            target.removeEventListener(
                "touchend",
                this._up,
                this.getEventListenerOptions()
            );
            target.removeEventListener(
                "touchcancel",
                this._up,
                this.getEventListenerOptions()
            );
        }
        this._eventTargets = [];
    }

    private getEventListenerOptions(): EventListenerOptions {
        return { capture: this._useCapture, passive: this._passive } as any;
    }

    public get useCapture(): boolean {
        return this._useCapture;
    }

    public set useCapture(useCapture) {
        if (this._useCapture === useCapture) return;
        this.cancel();
        this.removeEventListener();
        this._useCapture = useCapture;
        this.addEventListener();
    }

    public get passive(): boolean {
        return this._passive;
    }

    public set passive(passive) {
        if (this._passive === passive) return;
        this.cancel();
        this.removeEventListener();
        this._passive = passive;
        this.addEventListener();
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    public set enabled(enabled) {
        if (this._enabled === enabled) return;
        if (!enabled) this.cancel();
        this._enabled = enabled;
    }

    public get element(): HTMLElement {
        return this._element;
    }

    public set element(element) {
        if (this._element === element) return;
        if (this._element) {
            this.cancel();
            this.removeEventListener();
        }
        var old = this._element;
        this._element = element;
        if (this._element) this.addEventListener();
        this.onChangeElement(element, old);
    }

    protected onChangeElement(
        newElement: HTMLElement,
        oldElement: HTMLElement
    ): void {}

    public get rect(): DOMRect {
        return Actor.rect(this.pointers);
    }

    public get center(): DOMPoint {
        return Actor.center(this.pointers);
    }

    public get first(): DOMPoint {
        return this.pointers.length > 0
            ? new DOMPoint(this.pointers[0].x, this.pointers[0].y)
            : null;
    }

    protected getTime(): number {
        return Date.now();
        // return performance.now();
    }

    protected addPointer(e: ActorEvent): PointerInfo[] {
        if (e instanceof MouseEvent) {
            if (this.allowedMouseButtons.indexOf(e.button) === -1) {
                return null;
            }
            //Prevent adding the pointer multiple times
            if (this.getPointer((<any>e).pointerId || this._mouseEventId)) {
                return null;
            }
            var pointer = this.createPointer();
            pointer.id = (<any>e).pointerId || this._mouseEventId;

            this.updatePointer(pointer, PointerState.START, e, e);

            this._pointers.push(pointer);
            // for (var p of this._pointers)
            //     p.multipointer = this._pointers.length > 1;
            return [pointer];
        } else {
            var result: PointerInfo[] = [];
            for (var i = 0; i < e.changedTouches.length; ++i) {
                var t = e.changedTouches[i];
                if (this.getPointer(t.identifier)) {
                    continue;
                }
                var pointer = this.createPointer();
                pointer.id = t.identifier;
                this.updatePointer(pointer, PointerState.START, t, e);
                this._pointers.push(pointer);
                result.push(pointer);
            }

            // for (var p of this._pointers)
            //     p.multipointer = this._pointers.length > 1;

            return result.length > 0 ? result : null;
        }

        throw new Error("Could not add ActionEvent. Wrong type.");
    }

    protected movePointer(e: ActorEvent): PointerInfo[] {
        if (e instanceof MouseEvent) {
            var pointer = this.getPointer(
                (<any>e).pointerId || this._mouseEventId
            );
            if (!pointer) return null;
            this.updatePointer(pointer, PointerState.MOVE, e, e);
            return [pointer];
        } else {
            var result: PointerInfo[] = [];
            for (var i = 0; i < e.changedTouches.length; ++i) {
                var t = e.changedTouches[i];
                var pointer = this.getPointer(t.identifier);
                if (!pointer) continue;
                this.updatePointer(pointer, PointerState.MOVE, t, e);
                result.push(pointer);
            }
            return result.length > 0 ? result : null;
        }

        throw new Error("Could not update ActionEvent. Wrong type.");
    }

    protected removePointer(e: ActorEvent): PointerInfo[] {
        if (e instanceof MouseEvent) {
            var pointer = this.getPointer(
                (<any>e).pointerId || this._mouseEventId
            );
            if (!pointer) return null;
            pointer.event = e;
            this.updatePointer(pointer, PointerState.END, null, e);
            this._pointers.splice(this._pointers.indexOf(pointer), 1);
            return [pointer];
        } else {
            var result: PointerInfo[] = [];
            //TODO: check if e.touches is more reliable!!!
            //Escpecially when the target-element is removed during touch-phase.

            for (var i = 0; i < e.changedTouches.length; ++i) {
                var t = e.changedTouches[i];
                var pointer = this.getPointer(t.identifier);
                if (!pointer) {
                    continue;
                }
                pointer.event = e;
                this.updatePointer(pointer, PointerState.END, null, e);
                result.push(pointer);
                this._pointers.splice(this._pointers.indexOf(pointer), 1);
            }
            return result.length > 0 ? result : null;
        }

        throw new Error("Could not remove ActionEvent. Wrong type.");
    }

    protected updatePointer(
        pointer: PointerInfo,
        state: PointerState,
        data: PointerData,
        event: ActorEvent
    ): void {
        var time = this.getTime();

        pointer.state = state;
        pointer.event = event;

        if (state === PointerState.START) {
            pointer.deltaTime = 0;
            pointer.changeTime = time;
            pointer.x = data.clientX;
            pointer.y = data.clientY;
            pointer.deltaX = 0;
            pointer.deltaY = 0;
            pointer.originX = data.clientX;
            pointer.originY = data.clientY;
            pointer.moveX = 0;
            pointer.moveY = 0;
            pointer.distance = 0;
            pointer.velocityX = 0;
            pointer.velocityY = 0;
            pointer.creationTime = time;
            pointer.multitouch = this._pointers.length > 1;

            pointer.intervalTime = time;
            pointer.intervalX = pointer.x;
            pointer.intervalY = pointer.y;
        } else if (state === PointerState.MOVE) {
            //Fixes ie11 and maybe some other browsers
            if (pointer.x === data.clientX && pointer.y === data.clientY)
                return;

            pointer.deltaTime = time - pointer.changeTime;
            pointer.changeTime = time;
            pointer.deltaX = data.clientX - pointer.x;
            pointer.deltaY = data.clientY - pointer.y;
            pointer.moveX += Math.abs(pointer.deltaX);
            pointer.moveY += Math.abs(pointer.deltaY);
            pointer.distance = Math.sqrt(
                pointer.moveX * pointer.moveX + pointer.moveY * pointer.moveY
            );
            pointer.x = data.clientX;
            pointer.y = data.clientY;

            // var dt = Calc.clampMin(pointer.deltaTime, 0.0001);
            // pointer.velocityX = pointer.deltaX / dt;
            // pointer.velocityY = pointer.deltaY / dt;

            //Sample the velocity "every" 25ms
            var dt = Calc.clampMin(time - pointer.intervalTime, 0.0001);
            if (dt > 25 || (!pointer.velocityX && !pointer.velocityY)) {
                var deltaX = pointer.x - pointer.intervalX;
                var deltaY = pointer.y - pointer.intervalY;
                pointer.velocityX = deltaX / dt;
                pointer.velocityY = deltaY / dt;
                pointer.intervalX = pointer.x;
                pointer.intervalY = pointer.y;
                pointer.intervalTime = time;
            }
        } else if (
            state === PointerState.END ||
            state === PointerState.CANCEL
        ) {
            pointer.deltaTime = time - pointer.changeTime;
            pointer.changeTime = time;

            if (data) {
                pointer.x = data.clientX;
                pointer.y = data.clientY;
            } else {
                //The velocity should be zero after 250ms.
                //So when the user stops the cursor, the velocity decreases.
                var velocityDecrease =
                    1 - Calc.clampMax(pointer.deltaTime, 250) / 250;
                pointer.velocityX *= velocityDecrease;
                pointer.velocityY *= velocityDecrease;
            }
        }
    }

    private getPointer(id: number): PointerInfo {
        for (var info of this._pointers) if (info.id === id) return info;
        return null;
    }

    public get distance(): number {
        return Actor.distance(this._pointers);
    }

    public get pointers(): PointerInfo[] {
        return this._pointers;
    }

    public get count(): number {
        return this._pointers.length;
    }

    public static center(pointers: Pointer[]): DOMPoint {
        if (!pointers || pointers.length === 0) return null;

        if (pointers.length === 1) {
            return new DOMPoint(pointers[0].x, pointers[0].y);
        }

        var x = 0;
        var y = 0;
        for (var pointer of pointers) {
            x += pointer.x;
            y += pointer.y;
        }
        return new DOMPoint(x / pointers.length, y / pointers.length);
    }

    public static lifetime(pointers: Pointer[]): number {
        if (!pointers || pointers.length === 0) return 0;

        var minTime: number = Number.MAX_VALUE;
        for (var pointer of pointers) {
            minTime = Math.min(pointer.creationTime, minTime);
        }
        return Date.now() - minTime;
        // return performance.now() - minTime;
    }

    public static distance(pointers: Pointer[]): number {
        var bounds = this.rect(pointers);
        if (!bounds) return 0;
        return Math.sqrt(
            bounds.width * bounds.width + bounds.height * bounds.height
        );
    }

    public static angle(pointers: Pointer[]): number {
        //Trivial case: just a single pointer or no pointer.
        if (!pointers || pointers.length < 2) return 0;
        var a = pointers[0];
        var b = pointers[pointers.length - 1];
        return Math.atan2(a.y - b.y, a.x - b.x);
    }

    public static rect(pointers: Pointer[]): DOMRect {
        if (!pointers || pointers.length <= 1) return null;

        var minX = Number.MAX_VALUE;
        var maxX = -Number.MAX_VALUE;
        var minY = Number.MAX_VALUE;
        var maxY = -Number.MAX_VALUE;

        for (var pointer of pointers) {
            minX = Math.min(minX, pointer.x);
            maxX = Math.max(maxX, pointer.x);
            minY = Math.min(minY, pointer.y);
            maxY = Math.max(maxY, pointer.y);
        }

        return new DOMRect(minX, minY, maxX - minX, maxY - minY);
    }

    public dispose(): void {
        // if (this._element)
        this.removeEventListener();
        this._element = null;
        this.onDown.dispose();
        this.onDown = null;
        this.onMove.dispose();
        this.onMove = null;
        this.onChangeActive.dispose();
        this.onChangeActive = null;
        this.onUp.dispose();
        this.onClear.dispose();
        this.onClear = null;
        this.onUp = null;
        this._down = null;
        this._move = null;
        this._up = null;
        this._cancel = null;
        this._pointers = null;
        this.pointer = null;
        for (var disposable of this._disposables) {
            disposable.dispose();
        }
        this._disposables = null;
    }
}
